import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Alert, CircularProgress, Grid, Chip, Collapse, IconButton, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const API = '/api/contabilidad';
function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const RANGOS_KEYS = ['corriente','r31_60','r61_90','r91_180','r181_365','mayor365'];
const RANGOS_LABELS = ['0-30d','31-60d','61-90d','91-180d','181-365d','+1 año'];
const RANGO_COLORS = ['success','primary','warning','warning','error','error'];

export default function AgingCuentas() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [tipo, setTipo] = useState('CXC');
  const [fechaCorte, setFechaCorte] = useState(new Date().toISOString().substring(0,10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reporte, setReporte] = useState(null);
  const [expandidos, setExpandidos] = useState({});

  useEffect(() => { axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {}); }, []);

  async function generar() {
    if (!empresaId) return setError('Selecciona una empresa');
    setLoading(true); setError(''); setReporte(null); setExpandidos({});
    try {
      const r = await axios.get(`${API}/aging?empresa_id=${empresaId}&tipo=${tipo}&fecha_corte=${fechaCorte}`);
      setReporte(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setLoading(false);
  }

  function toggleExpand(codigo) { setExpandidos(prev => ({ ...prev, [codigo]: !prev[codigo] })); }

  const estadoChip = (dias) => {
    if (dias <= 30) return { label: 'Corriente', color: 'success' };
    if (dias <= 60) return { label: '31-60d', color: 'primary' };
    if (dias <= 90) return { label: '61-90d', color: 'warning' };
    if (dias <= 180) return { label: '91-180d', color: 'warning' };
    if (dias <= 365) return { label: '181-365d', color: 'error' };
    return { label: '+1 año', color: 'error' };
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <AccountBalanceWalletIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Antigüedad de Saldos (Aging)</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Análisis de vencimiento de cuentas por cobrar (12x) y cuentas por pagar (42x).
      </Typography>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Tipo" value={tipo} onChange={e => { setTipo(e.target.value); setReporte(null); }} sx={{ minWidth: '20em' }}>
              <MenuItem value="CXC">Cuentas por Cobrar (12x)</MenuItem>
              <MenuItem value="CXP">Cuentas por Pagar (42x)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField size="small" label="Fecha de Corte" type="date" value={fechaCorte} onChange={e => setFechaCorte(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: '20em' }} />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button variant="contained" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />} onClick={generar} disabled={!empresaId || loading}>
              Generar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {reporte && (
        <Box>
          {/* Resumen de rangos */}
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {RANGOS_KEYS.map((k, i) => (
              <Grid key={k} item xs={6} sm={4} md={2}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, borderColor: reporte.totales[k] > 0 ? `${RANGO_COLORS[i]}.main` : 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block">{RANGOS_LABELS[i]}</Typography>
                  <Typography fontWeight="bold" color={reporte.totales[k] > 0 ? `${RANGO_COLORS[i]}.main` : 'text.disabled'}>
                    S/ {fmt(reporte.totales[k])}
                  </Typography>
                </Paper>
              </Grid>
            ))}
            <Grid item xs={12} sm={12} md={2}>
              <Paper sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.8)' }}>TOTAL</Typography>
                <Typography fontWeight="bold" variant="h6">S/ {fmt(reporte.totales.total)}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {reporte.cuentas.length === 0 ? (
            <Alert severity="info">No se encontraron saldos pendientes para los criterios seleccionados.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} width={40} />
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cuenta</TableCell>
                    {RANGOS_LABELS.map((l, i) => (
                      <TableCell key={i} align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{l}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Docs.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reporte.cuentas.map(c => (
                    <React.Fragment key={c.cuenta_codigo}>
                      <TableRow hover sx={{ cursor: 'pointer', bgcolor: expandidos[c.cuenta_codigo] ? 'action.hover' : 'inherit' }}
                        onClick={() => toggleExpand(c.cuenta_codigo)}>
                        <TableCell>
                          <IconButton size="small">{expandidos[c.cuenta_codigo] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>{c.cuenta_codigo}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.cuenta_nombre}</Typography>
                        </TableCell>
                        {RANGOS_KEYS.map((k, i) => (
                          <TableCell key={k} align="right" sx={{ color: c[k] > 0 ? `${RANGO_COLORS[i]}.main` : 'text.disabled', fontWeight: c[k] > 0 ? 'bold' : 'normal' }}>
                            {c[k] > 0 ? `S/ ${fmt(c[k])}` : '—'}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>S/ {fmt(c.total)}</TableCell>
                        <TableCell align="center"><Chip label={c.documentos.length} size="small" /></TableCell>
                      </TableRow>

                      {/* Detalle documentos */}
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0 }}>
                          <Collapse in={!!expandidos[c.cuenta_codigo]} timeout="auto" unmountOnExit>
                            <Box sx={{ m: 1, ml: 4, overflowX: 'auto' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell>Glosa / Referencia</TableCell>
                                    <TableCell align="right">Saldo</TableCell>
                                    <TableCell align="center">Días</TableCell>
                                    <TableCell align="center">Rango</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {c.documentos.map((d, i) => {
                                    const chip = estadoChip(d.dias);
                                    return (
                                      <TableRow key={i} hover>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{d.fecha}</TableCell>
                                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{d.glosa}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>S/ {fmt(d.saldo)}</TableCell>
                                        <TableCell align="center">{d.dias}</TableCell>
                                        <TableCell align="center">
                                          <Chip label={chip.label} size="small" color={chip.color} />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}

                  {/* Fila totales */}
                  <TableRow sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>TOTAL GENERAL</TableCell>
                    {RANGOS_KEYS.map((k, i) => (
                      <TableCell key={k} align="right" sx={{ fontWeight: 'bold', color: reporte.totales[k] > 0 ? `${RANGO_COLORS[i]}.main` : 'text.disabled' }}>
                        {reporte.totales[k] > 0 ? `S/ ${fmt(reporte.totales[k])}` : '—'}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>S/ {fmt(reporte.totales.total)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
}

